import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { FieldError, fieldErrors, Notice, ProblemAlert } from "@/components/site/Feedback";
import { Turnstile, useTurnstileState } from "@/components/site/Turnstile";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/hooks";
import { legalEntity, supportEmail } from "@/lib/config";

export function ContactPage() {
  usePageTitle("Contact Us");
  const { user } = useAuth();
  const turnstile = useTurnstileState();
  const [form, setForm] = useState({
    name: user?.fullName ?? "",
    email: user?.email ?? "",
    subject: "",
    message: "",
  });
  const send = useMutation({
    mutationFn: () =>
      api.contact(
        { name: form.name.trim(), email: form.email.trim(), subject: form.subject.trim(), message: form.message.trim() },
        turnstile.token,
      ),
    onSettled: () => turnstile.reset(),
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Talk to the Academy team"
        subtitle="Questions about membership, mentorship schemes, deal sourcing or your account — we reply within two working days."
      />
      <Section>
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <div className="space-y-6 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 size-5 text-gold" />
              <div>
                <p className="font-medium text-foreground">Email</p>
                <a href={`mailto:${supportEmail}`} className="underline underline-offset-4">
                  {supportEmail}
                </a>
              </div>
            </div>
            <p>
              {legalEntity}, trading as Sir Levantine Academy, United Kingdom. Our support team works Monday to
              Friday, 9:00–17:30 UK time, excluding bank holidays.
            </p>
            <p>
              For data protection requests (access, correction, deletion) please mention "Data request" in the subject
              line.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-card p-8 shadow-elevated">
            {send.isSuccess ? (
              <Notice tone="success" title="Message sent">
                Thank you — we'll get back to you at {form.email}.
              </Notice>
            ) : (
              <form
                className="space-y-5"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!turnstile.missing) send.mutate();
                }}
              >
                <ProblemAlert error={send.error} />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" autoComplete="name" maxLength={100} value={form.name} onChange={set("name")} />
                    <FieldError messages={fieldErrors(send.error, "Name")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" autoComplete="email" value={form.email} onChange={set("email")} />
                    <FieldError messages={fieldErrors(send.error, "Email")} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input id="subject" maxLength={200} value={form.subject} onChange={set("subject")} />
                  <FieldError messages={fieldErrors(send.error, "Subject")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" rows={7} maxLength={5000} value={form.message} onChange={set("message")} />
                  <FieldError messages={fieldErrors(send.error, "Message")} />
                </div>
                <Turnstile ref={turnstile.ref} onToken={turnstile.setToken} onEnabled={turnstile.setEnabled} />
                <Button
                  type="submit"
                  variant="gold"
                  size="lg"
                  className="w-full"
                  disabled={send.isPending || turnstile.missing || !form.name || !form.email || !form.subject || !form.message}
                >
                  {send.isPending && <Loader2 className="animate-spin" />}
                  Send message
                </Button>
              </form>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
